class Sharing {
	static TIMELINE = 1;
	static LAYER = 2;
	static RESOURCE = 3;
	static GRAPHIC = 4;

	static VIEW = 1;
	static EDIT = 2;

	constructor (type) {
		this.thingType = type;
		this.thingId = null;

		this.units = [];
		this.callback = null;
		this.buttons = new Map();

		this.popup = null;
		this._body = null;
	}

	get endPoint() {
		switch (this.thingType) {
			case Sharing.LAYER: return 'layers';
			case Sharing.RESOURCE: return 'items';
			case Sharing.GRAPHIC: return 'graphics';
			default: throw new Error("Invalid type.");
		}
	}

	button(thingId, callback) {
		let counter = UI.span('share-count');
		counter.hidden = true;
		let entry = { counter, thingId };
		let btn = UI.htm(
			'button',
			'ed-pri-i-btn px-2 py-1 leading-none relative',
			[UI.icon('share', 18), counter],
			{
				'mousedown': ev => stopEv(ev),
				'click': ev => this.open(entry.thingId, ev.currentTarget, callback)
			}
		);
		entry.btn = btn;
		this.buttons.set(thingId, entry);
		return btn;
	}

	updateButtonId(oldId, newId) {
		let entry = this.buttons.get(oldId);
		if (entry) {
			this.buttons.delete(oldId);
			entry.thingId = newId;
			this.buttons.set(newId, entry);
		}
	}

	setCount(thingId, count) {
		let entry = this.buttons.get(thingId);
		if (!entry) return;
		this._updateCounter(entry.counter, count);
	}

	_updateCounter(counter, count) {
		if (count > 0) {
			counter.textContent = count;
			counter.hidden = false;
		} else {
			counter.hidden = true;
		}
	}

	updateCounter() {
		if (!this.thingId) return;
		let entry = this.buttons.get(this.thingId);
		if (!entry) return;

		let count = 0;
		for (let u of this.units) {
			if (u.value) count++;
		}
		this._updateCounter(entry.counter, count);
	}

	open(thingId, button, callback) {
		if (this.popup && this.popup._alignedTo === button) return;

		if (typeof callback != 'undefined') this.callback = callback;
		else this.callback = null;

		this.thingId = thingId;
		this.units = [];

		this._ensurePopup();
		this._body.innerHTML = '';
		this.popup.handle(button);

		API.fetch(`${this.endPoint}/${this.thingId}/sharing`)
		.then(reply => {
			if (reply.units && Array.isArray(reply.units)) for (let data of reply.units) {
				let unit = new SharingUnit(data, this);
				this.units.push(unit);
				this._body.appendChild(unit.elem);
			}

			if (reply.sharing && Array.isArray(reply.sharing)) for (let data of reply.sharing) {
				this.getUnit(data.entity_id).init(data);
			}
			this.update();
			this.updateCounter();
		});
	}

	getUnit(id) {
		for (let u of this.units) {
			if (u.id == id) return u;
		}
		return false;
	}

	_ensurePopup() {
		if (this.popup == null) {
			this._body = UI.div();
			this.popup = Dialog.Popup(
				UI.div('p-3 min-w-64', [
					UI.div('flex gap-3 pb-1 items-center', [
						UI.div('flex-0 w-5', ''),
						UI.div('flex-1', ''),
						UI.span('flex-0 w-12 text-center text-xs text-neutral-500', _('VIEW')),
						UI.span('flex-0 w-12 text-center text-xs text-neutral-500', _('EDIT'))
					]),
					this._body
				])
			).sticky();
		}
	}

	update() {
		this.units.forEach(unit => unit.update());
	}
}

class SharingUnit {
	static CIRCLE = 1;
	static COMMUNITY = 2;

	constructor(data, controller) {
		this.id = data.unit_id;
		this.name = data.own ? _('My Circle') : data.unit_name;
		this.controller = controller;

		this._prevView = this._view = 0;
		this._prevEdit = this._edit = 0;
		this._elem = null;

		switch (data.unit_type) {
			case Nav.CIRCLE:
				this.icon = 'circle';
				break;
			case Nav.COMMUNITY:
				this.icon = 'community';
				break;
		}

		this.viewChk = UI.chk(
			null,
			{
				'change': () => {
					this.view = this.viewChk.checked;
					this.controller.update();
					this.save();
				}
			}
		);

		this.editChk = UI.chk(
			null,
			{
				'change': () => {
					this.edit = this.editChk.checked;
					this.controller.update();
					this.save();
				}
			}
		);
	}

	get elem() {
		if (this._elem == null) {
			this._elem = UI.div(
				'py-1 flex gap-3 items-center border-t border-neutral-200',
				[
					UI.div('flex-0', UI.icon(this.icon, 20)),
					UI.div('flex-1 text-sm', this.name),
					UI.div('flex-0 w-12 text-center', this.viewChk),
					UI.div('flex-0 w-12 text-center', this.editChk)
				]
			);
		}

		return this._elem;
	}

	get view() {
		return this._view;
	}

	set view(v) {
		if (!v && this.edit) this.edit = 0;
		this._view = v;
	}

	get edit() {
		return this._edit;
	}

	set edit(v) {
		if (v && !this.view) this.view = 1;
		this._edit = v;
	}

	get prev() {
		if (this._prevEdit) return 2;
		if (this._prevView) return 1;
		return 0;
	}

	get value() {
		if (this._edit) return 2;
		if (this._view) return 1;
		return 0;
	}

	get values() {
		return {
			id: this.id,
			shareMode: this.value
		};
	}

	get changed() {
		return this.value !== this.prev;
	}

	init(data) {
		this._prevView = this._view = (data.share_mode >= Sharing.VIEW) ? 1 : 0;
		this._prevEdit = this._edit = (data.share_mode >= Sharing.EDIT) ? 1 : 0;
	}

	update() {
		this.viewChk.checked = this.view;
		this.editChk.checked = this.edit;
	}

	save() {
		let prevView = this._prevView;
		let prevEdit = this._prevEdit;

		this._prevView = this._view;
		this._prevEdit = this._edit;

		API.insert(
			`${this.controller.endPoint}/${this.controller.thingId}/sharing`,
			{ changes: [this.values] }
		)
		.then(reply => {
			if (showError(reply)) {
				this._prevView = this._view = prevView;
				this._prevEdit = this._edit = prevEdit;
				this.controller.update();
				this.controller.updateCounter();
				return;
			}
			if (reply.toast) makeToast(reply.toast);
			this.controller.updateCounter();

			if (this.controller.callback) {
				let shareCount = 0;
				for (let u of this.controller.units) {
					if (u.value) shareCount++;
				}
				this.controller.callback(shareCount);
			}
		});
	}
}
